import React from 'react';
import { Shield, Lock, Database, Eye, FileText, AlertCircle } from 'lucide-react';

const Privacy = () => {
  const sections = [
    {
      icon: FileText,
      title: "1. Introduction",
      content: 'Twe Voice Assistant ("Twe", "we", "our", or "us") is a desktop AI voice assistant developed and operated by Đặng Việt Tú, designed to help users interact with their computers more efficiently using voice commands, AI-powered assistance, and automation features. We are committed to protecting your privacy and handling your data in a transparent, secure, and responsible manner. By using our website or desktop application, you agree to the practices described in this Privacy Policy.'
    },
    {
      icon: Database,
      title: "2. Information We Collect",
      subsections: [
        {
          title: "2.1 Information You Provide",
          items: [
            "Email address (for authentication via Google or Supabase)",
            "Account identifiers",
            "Payment-related metadata (handled by third-party payment processors — we do not store card details)",
            "Support messages or feedback you send us"
          ]
        },
        {
          title: "2.2 Automatically Collected Information",
          items: [
            "Device and operating system type",
            "App version and usage statistics",
            "Error logs and crash reports",
            "IP address (used only for security and fraud prevention)"
          ]
        },
        {
          title: "2.3 Voice & AI Data",
          items: [
            "Voice inputs are processed only to fulfill the requested command",
            "We do not store raw voice recordings by default",
            "AI prompts may be temporarily processed by trusted AI providers (e.g., OpenAI) to generate responses"
          ]
        }
      ]
    },
    {
      icon: Eye,
      title: "3. How We Use Your Information",
      items: [
        "Authenticate users and manage accounts",
        "Provide AI-powered voice features",
        "Improve app performance and reliability",
        "Monitor usage for billing and abuse prevention",
        "Communicate important updates and security notices"
      ]
    },
    {
      icon: Lock,
      title: "4. Third-Party Services",
      content: "Twe integrates with trusted third-party services, including Supabase (authentication & database), Google OAuth (login), AI providers (for natural language processing), and Payment providers (for subscriptions). Each third party processes data according to their own privacy policies."
    },
    {
      icon: Shield,
      title: "5. Data Security",
      content: "We implement industry-standard security practices, including encrypted connections (HTTPS), secure authentication flows, server-side protection of API keys, and minimal data retention. However, no system is 100% secure, and we cannot guarantee absolute security."
    },
    {
      icon: AlertCircle,
      title: "6. Your Rights",
      content: "Depending on your jurisdiction, you may have the right to access your data, request correction or deletion, withdraw consent, and request data portability. You can contact us at contact@twe-assistant.com."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <Shield className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-400">
            Last updated: January 1, 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 hover:border-orange-500/30 transition-all duration-300"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">
                    {section.title}
                  </h2>
                </div>

                {section.content && (
                  <p className="text-gray-300 leading-relaxed">
                    {section.content}
                  </p>
                )}

                {section.items && (
                  <ul className="space-y-3 mt-4">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <span className="text-orange-500 mt-1">•</span>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.subsections && (
                  <div className="space-y-6 mt-4">
                    {section.subsections.map((sub, idx) => (
                      <div key={idx}>
                        <h3 className="text-lg font-semibold text-white mb-3">
                          {sub.title}
                        </h3>
                        <ul className="space-y-2">
                          {sub.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start space-x-3">
                              <span className="text-orange-500 mt-1">•</span>
                              <span className="text-gray-300">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Changes to Policy */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              7. Changes to This Policy
            </h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. Continued use of the app constitutes acceptance of the updated policy.
            </p>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-12 text-center bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-3">
            Questions About Privacy?
          </h3>
          <p className="text-gray-300 mb-4">
            If you have any questions or concerns about our privacy practices
          </p>
          <a
            href="mailto:contact@twe-assistant.com"
            className="text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200"
          >
            contact@twe-assistant.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default Privacy;